/** @jsxImportSource @emotion/react */
import { mostReadable, themeColors, darkenBy, setAlpha, darkPalette } from 'bc-ods';
import type { ReactElement, JSX } from 'react';
import { cx } from '@emotion/css';

interface PropTypes {
  content: JSX.Element | string;
  color?: string;
  className?: string;
  active?: boolean;
  isApi?: boolean;
}

const colorStyles: { backgroundColor: string; color: string }[] = [
  {
    backgroundColor: darkPalette.purple,
    color: '#FFFFF',
  },
  {
    backgroundColor: darkPalette.midnight,
    color: '#FFFFF',
  },
  {
    backgroundColor: darkPalette.blue,
    color: '#FFF',
  },
  {
    backgroundColor: darkPalette.aqua,
    color: '#000',
  },
  {
    backgroundColor: darkPalette.green,
    color: '#FFF',
  },
  {
    backgroundColor: darkPalette.yellow,
    color: '#000',
  },
  {
    backgroundColor: darkPalette.orange,
    color: '#000',
  },
  {
    backgroundColor: darkPalette.red,
    color: '#FFF',
  },
  {
    backgroundColor: darkPalette.magenta,
    color: '#000',
  },
  {
    backgroundColor: darkPalette.brown,
    color: '#FFF',
  },
];

function getHashCode(str: string): number {
  return [...str].reduce((acc, char) => acc + char.charCodeAt(0), 0);
}

function getAvatarColorStyles(name: string): { backgroundColor: string; color: string } {
  const index = getHashCode(name) % colorStyles.length;
  return colorStyles[index];
}

function Avatar(props: PropTypes): ReactElement {
  const { content, className, active, isApi } = props;

  let color = props.color ?? themeColors.border;

  if (active) {
    color = darkenBy(color, 0.15).toString();
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={cx('user-select-none', className, { 'rounded-circle': !isApi })}
      viewBox="0 0 48 48"
      css={{
        transition: 'box-shadow 150ms ease-in-out',
        circle: {
          transition: 'fill 150ms ease-in-out',
        },
        ...(active && {
          boxShadow: `0 0 0 0.1875rem ${setAlpha(color, 0.3)}`,
        }),
      }}
    >
      {isApi ? (
        <path
          d="M26.57 2.155c.893.103 1.564.825 1.675 1.718l.24 1.934c.07.55.474 1.005 1.006 1.177a18.2 18.2 0 0 1 2.493 1.014c.49.25 1.091.215 1.53-.128l1.572-1.22c.705-.55 1.684-.594 2.389-.044.24.19.473.378.705.576l2.638 2.655.464.568c.55.704.516 1.693-.034 2.397l-1.203 1.539a1.444 1.444 0 0 0-.13 1.546c.413.808.766 1.66 1.058 2.536.172.533.627.928 1.177.997l1.96.24c.885.112 1.606.782 1.718 1.667l.026.224.026.223v4.469l-.026.223-.026.224c-.103.885-.834 1.555-1.719 1.667l-1.959.24a1.437 1.437 0 0 0-1.177.997 17.318 17.318 0 0 1-1.057 2.536c-.25.498-.215 1.108.129 1.547l1.203 1.538c.55.704.593 1.693.034 2.397l-.464.568-2.638 2.638c-.232.198-.464.387-.705.576-.705.541-1.684.507-2.39-.043l-1.554-1.22A1.447 1.447 0 0 0 32 40c-.8.396-1.633.74-2.492 1.015a1.447 1.447 0 0 0-1.006 1.186l-.24 1.933c-.112.885-.783 1.616-1.676 1.719-.843.094-1.702.146-2.57.146-.954 0-1.899-.06-2.819-.18-.885-.112-1.546-.834-1.658-1.72l-.25-1.984c-.068-.55-.463-.997-.988-1.178a17.238 17.238 0 0 1-2.397-1.005c-.499-.25-1.109-.215-1.547.129l-1.547 1.203c-.705.55-1.693.584-2.398.034a21.813 21.813 0 0 1-3.815-3.841c-.542-.705-.507-1.685.043-2.39l1.22-1.563a1.448 1.448 0 0 0 .129-1.53 17.27 17.27 0 0 1-.971-2.363 1.438 1.438 0 0 0-1.178-.997l-1.959-.25c-.885-.111-1.607-.781-1.719-1.667A21.564 21.564 0 0 1 2 24c0-.92.06-1.822.146-2.698.112-.886.834-1.556 1.719-1.668l1.96-.249a1.438 1.438 0 0 0 1.177-.997c.266-.816.593-1.598.97-2.354.25-.499.215-1.092-.128-1.53l-1.22-1.573c-.55-.704-.585-1.684-.044-2.389a21.815 21.815 0 0 1 3.816-3.841c.705-.55 1.693-.516 2.398.034l1.547 1.203a1.443 1.443 0 0 0 1.546.13 17.715 17.715 0 0 1 2.398-1.006c.524-.18.92-.628.988-1.178l.25-1.985c.111-.885.773-1.607 1.658-1.719a21.775 21.775 0 0 1 5.389-.025Z"
          fill={color}
        />
      ) : (
        <circle cx="24" cy="24" r="24" fill={color} />
      )}
      <text
        x={24}
        y={26}
        dominantBaseline="middle"
        textAnchor="middle"
        css={{
          font: '600 20px var(--ods-body-font-family)',
          textTransform: 'uppercase',
          fill: `${mostReadable(color).toString()}`,
          letterSpacing: '0.5px',
        }}
      >
        {content}
      </text>
    </svg>
  );
}

export default Avatar;

export { getAvatarColorStyles };
